import { z } from 'zod'
import { ComponentCategory, InstallPosition } from '../enums'
import { Id, Timestamp } from './common'
import { checkSpecs, Currency, optionalText } from './component'

export const WishlistStatus = z.enum(['wanted', 'bought', 'dropped'])
export type WishlistStatus = z.infer<typeof WishlistStatus>

export const WishlistItem = z.object({
  id: Id,
  bike_id: Id.nullable(),
  position: InstallPosition,
  category: ComponentCategory,
  brand: z.string().nullable(),
  model: z.string().nullable(),
  url: z.string().nullable(),
  /** Minor units (pence). */
  price_minor: z.number().int().nullable(),
  currency: z.string().nullable(),
  weight_g: z.number().int().nullable(),
  specs: z.record(z.string(), z.unknown()),
  notes: z.string().nullable(),
  status: WishlistStatus,
  /** The part it became, once bought. */
  component_id: Id.nullable(),
  created_at: Timestamp,
  updated_at: Timestamp,
})
export type WishlistItem = z.infer<typeof WishlistItem>

export const WishlistList = z.object({ items: z.array(WishlistItem) })
export type WishlistList = z.infer<typeof WishlistList>

export const WishlistQuery = z.object({
  bike_id: Id.optional(),
  status: WishlistStatus.optional(),
})

const itemFields = {
  bike_id: Id.nullable().optional(),
  position: InstallPosition.default('none'),
  brand: optionalText(80),
  model: optionalText(120),
  url: z
    .url({ protocol: /^https?$/, message: 'Paste a web link starting with http or https.' })
    .max(2000)
    .nullable()
    .optional(),
  price_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
  currency: Currency.nullable().optional(),
  weight_g: z.number().int().min(0).max(100_000).nullable().optional(),
  specs: z.record(z.string(), z.unknown()).default({}),
  notes: optionalText(2000),
}

export const CreateWishlistBody = z
  .object({ category: ComponentCategory, ...itemFields })
  .superRefine((b, ctx) => checkSpecs(b.category, b.specs, ctx))
export type CreateWishlistBody = z.input<typeof CreateWishlistBody>

/** Category cannot change; status goes wanted ⇄ dropped here, and to bought via /bought. */
export const UpdateWishlistBody = z
  .object({
    ...itemFields,
    position: InstallPosition.optional(),
    specs: z.record(z.string(), z.unknown()).optional(),
    status: z.enum(['wanted', 'dropped']).optional(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateWishlistBody = z.input<typeof UpdateWishlistBody>

export const BoughtBody = z.object({
  purchase_date: z.iso.date().optional(),
  /** What it actually cost, if not the wishlist price. */
  price_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
})
export type BoughtBody = z.input<typeof BoughtBody>

export const BoughtResponse = z.object({ item: WishlistItem, component_id: Id })
export type BoughtResponse = z.infer<typeof BoughtResponse>
